import { useState } from "react";
import type { CropVariety, UpdateCropVarietyDto } from "../../services/cropVarietiesApi";
import { useCropTypeById } from "../../hooks/useCropTypes";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Edit2, ChevronDown, ChevronUp } from "lucide-react";
import { useNotification } from "@/shared/hooks/useNotification";
import { getFieldErrors, getErrorMessage } from "@/shared/utils/errorHandler";
import { useSubmitSetupApproval } from "../../hooks/useSetupApprovals";
import { SETUP_APPROVAL_TYPE } from "../../services/setupApprovalApi";
import { useConfirm } from "@/shared/contexts/confirm-context";

interface EditCropVarietyDialogProps {
  cropVariety: CropVariety;
}

type DayField = keyof Pick<
  UpdateCropVarietyDto,
  | "sowingToGerminationDays"
  | "sowingToSeedlingDays"
  | "sowingToTransplantReadyDays"
  | "sowingToOvergrownDays"
  | "acclimationDays"
  | "plantingToMaturityDays"
  | "harvestingPeriodDays"
>;

const toStr = (val?: number) => (val == null ? "" : String(val));
const parseDay = (val: string) => (val === "" ? undefined : parseInt(val, 10));
const parseDecimal = (val: string) => (val === "" ? undefined : parseFloat(val));

function validateLifecycle(
  data: UpdateCropVarietyDto
): Record<string, string[]> | null {
  const errors: Record<string, string[]> = {};

  const germ = data.sowingToGerminationDays;
  const seedling = data.sowingToSeedlingDays;
  const transplant = data.sowingToTransplantReadyDays;
  const overgrown = data.sowingToOvergrownDays;

  const allDayFields: [DayField, number | undefined][] = [
    ["sowingToGerminationDays", germ],
    ["sowingToSeedlingDays", seedling],
    ["sowingToTransplantReadyDays", transplant],
    ["sowingToOvergrownDays", overgrown],
    ["acclimationDays", data.acclimationDays],
    ["plantingToMaturityDays", data.plantingToMaturityDays],
    ["harvestingPeriodDays", data.harvestingPeriodDays],
  ];

  for (const [field, val] of allDayFields) {
    if (val !== undefined && val < 1) {
      errors[field] = ["Must be at least 1 day"];
    }
  }

  if (data.stockPercent !== undefined && (data.stockPercent < 0 || data.stockPercent > 100)) {
    errors.stockPercent = ["Must be between 0 and 100"];
  }

  return Object.keys(errors).length > 0 ? errors : null;
}

export const EditCropVarietyDialog = ({
  cropVariety,
}: EditCropVarietyDialogProps) => {
  const [open, setOpen] = useState(false);
  const [showLifecycle, setShowLifecycle] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  // Use string state for day fields so inputs work naturally, parse on submit
  const [dayFields, setDayFields] = useState<Record<DayField, string>>({
    sowingToGerminationDays: toStr(cropVariety.sowingToGerminationDays),
    sowingToSeedlingDays: toStr(cropVariety.sowingToSeedlingDays),
    sowingToTransplantReadyDays: toStr(cropVariety.sowingToTransplantReadyDays),
    sowingToOvergrownDays: toStr(cropVariety.sowingToOvergrownDays),
    acclimationDays: toStr(cropVariety.acclimationDays),
    plantingToMaturityDays: toStr(cropVariety.plantingToMaturityDays),
    harvestingPeriodDays: toStr(cropVariety.harvestingPeriodDays),
  });

  const [formData, setFormData] = useState({
    name: cropVariety.name,
    description: cropVariety.description ?? "",
    isActive: cropVariety.isActive,
  });
  const [stockPercentInput, setStockPercentInput] = useState(toStr(cropVariety.stockPercent));

  const submitMutation = useSubmitSetupApproval();
  const parentCropTypeQuery = useCropTypeById(cropVariety.cropTypeId);
  const parentCropType = parentCropTypeQuery.data;
  const notify = useNotification();
  const confirm = useConfirm();
  const inheritedStockPercent = parentCropType?.stockPercent ?? 70;

  const handleDayChange = (field: DayField, value: string) => {
    setDayFields((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleChange = (
    field: keyof typeof formData,
    value: string | boolean
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field as string]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field as string];
        return next;
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});

    const payload: UpdateCropVarietyDto = {
      id: cropVariety.id,
      name: formData.name,
      description: formData.description || undefined,
      isActive: formData.isActive,
      sowingToGerminationDays: parseDay(dayFields.sowingToGerminationDays),
      sowingToSeedlingDays: parseDay(dayFields.sowingToSeedlingDays),
      sowingToTransplantReadyDays: parseDay(dayFields.sowingToTransplantReadyDays),
      sowingToOvergrownDays: parseDay(dayFields.sowingToOvergrownDays),
      acclimationDays: parseDay(dayFields.acclimationDays),
      plantingToMaturityDays: parseDay(dayFields.plantingToMaturityDays),
      harvestingPeriodDays: parseDay(dayFields.harvestingPeriodDays),
      stockPercent: parseDecimal(stockPercentInput),
    };

    const lifecycleErrors = validateLifecycle(payload);
    if (lifecycleErrors) {
      setFieldErrors(lifecycleErrors);
      setShowLifecycle(true);
      return;
    }

    confirm({
      title: "Submit changes for approval?",
      description: `Changes to “${cropVariety.name}” will be submitted for approval before they take effect.`,
      confirmLabel: "Submit",
      onConfirm: () =>
        submitMutation
          .mutateAsync({
            approvalType: SETUP_APPROVAL_TYPE.CropVariety,
            entityJson: JSON.stringify(payload),
            existingEntityId: cropVariety.id,
            description: `Update Crop Variety: ${cropVariety.name}`,
          })
          .then(() => {
            notify.success("Submitted for approval");
            setOpen(false);
          })
          .catch((error: unknown) => {
            const fe = getFieldErrors(error);
            if (fe) {
              setFieldErrors(fe);
            } else {
              notify.error(getErrorMessage(error));
            }
            throw error;
          }),
    });
  };

  const lifecycleFields: { key: DayField; label: string }[] = [
    { key: "sowingToGerminationDays", label: "Days to Germination" },
    { key: "sowingToSeedlingDays", label: "Days to Seedling" },
    { key: "sowingToTransplantReadyDays", label: "Days to Transplant-Ready" },
    { key: "sowingToOvergrownDays", label: "Days to Overgrown" },
    { key: "acclimationDays", label: "Acclimation Days" },
    { key: "plantingToMaturityDays", label: "Days to Maturity (field)" },
    { key: "harvestingPeriodDays", label: "Harvest Window (days)" },
  ];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="w-full justify-start">
          <Edit2 className="w-4 h-4 mr-2" />
          Edit
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Crop Variety</DialogTitle>
          <DialogDescription>{cropVariety.cropTypeName} — {cropVariety.name}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Field>
            <FieldLabel htmlFor="name">
              Variety Name <span className="text-red-500">*</span>
            </FieldLabel>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              required
              disabled={submitMutation.isPending}
            />
            {fieldErrors.name && (
              <div className="text-red-500 text-xs mt-1">
                {fieldErrors.name.join(", ")}
              </div>
            )}
          </Field>

          <Field>
            <FieldLabel htmlFor="description">Description</FieldLabel>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              disabled={submitMutation.isPending}
            />
          </Field>

          <Field>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="isActive"
                checked={formData.isActive}
                onCheckedChange={(checked) =>
                  handleChange("isActive", checked as boolean)
                }
                disabled={submitMutation.isPending}
              />
              <Label htmlFor="isActive" className="cursor-pointer">
                Active
              </Label>
            </div>
          </Field>

          {/* Lifecycle Defaults — collapsible */}
          <div className="border rounded-md">
            <button
              type="button"
              className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium hover:bg-muted/50 transition-colors"
              onClick={() => setShowLifecycle((v) => !v)}
            >
              Lifecycle Defaults
              {showLifecycle ? (
                <ChevronUp className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              )}
            </button>

            {showLifecycle && (
              <div className="px-4 pb-4 space-y-3 border-t">
                <div className="flex items-center justify-between pt-3">
                  <p className="text-xs text-muted-foreground">
                    All fields optional. Ordering must be: Germination &lt; Seedling &lt; Transplant-Ready &lt; Overgrown.
                  </p>
                  {parentCropType && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="text-xs shrink-0 ml-2"
                      onClick={() => {
                        setDayFields({
                          sowingToGerminationDays: toStr(parentCropType.sowingToGerminationDays),
                          sowingToSeedlingDays: toStr(parentCropType.sowingToSeedlingDays),
                          sowingToTransplantReadyDays: toStr(parentCropType.sowingToTransplantReadyDays),
                          sowingToOvergrownDays: toStr(parentCropType.sowingToOvergrownDays),
                          acclimationDays: toStr(parentCropType.acclimationDays),
                          plantingToMaturityDays: toStr(parentCropType.plantingToMaturityDays),
                          harvestingPeriodDays: toStr(parentCropType.harvestingPeriodDays),
                        });
                      }}
                    >
                      Copy from {parentCropType.name}
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 [&>*]:min-w-0">
                  {lifecycleFields.map(({ key, label }) => (
                    <Field key={key}>
                      <FieldLabel htmlFor={key}>{label}</FieldLabel>
                      <Input
                        id={key}
                        type="number"
                        min={1}
                        placeholder="days"
                        value={dayFields[key]}
                        onChange={(e) => handleDayChange(key, e.target.value)}
                        disabled={submitMutation.isPending}
                      />
                      {fieldErrors[key] && (
                        <div className="text-red-500 text-xs mt-1">
                          {fieldErrors[key].join(", ")}
                        </div>
                      )}
                    </Field>
                  ))}
                </div>

                <Field>
                  <FieldLabel htmlFor="stockPercent">Stock %</FieldLabel>
                  <Input
                    id="stockPercent"
                    type="number"
                    min={0}
                    max={100}
                    step={0.1}
                    placeholder={String(inheritedStockPercent)}
                    value={stockPercentInput}
                    onChange={(e) => {
                      setStockPercentInput(e.target.value);
                      if (fieldErrors.stockPercent) {
                        setFieldErrors((prev) => {
                          const next = { ...prev };
                          delete next.stockPercent;
                          return next;
                        });
                      }
                    }}
                    disabled={submitMutation.isPending}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Override the crop type default. Filling Reserve:{" "}
                    {(() => {
                      const v = parseFloat(stockPercentInput);
                      const pct = Number.isFinite(v) ? v : inheritedStockPercent;
                      return (100 - pct).toFixed(1);
                    })()}
                    %. Leave blank to inherit.
                  </p>
                  {fieldErrors.stockPercent && (
                    <div className="text-red-500 text-xs mt-1">
                      {fieldErrors.stockPercent.join(", ")}
                    </div>
                  )}
                </Field>
              </div>
            )}
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={submitMutation.isPending}
          >
            {submitMutation.isPending ? "Submitting..." : "Submit for Approval"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
